import type { RunSummary } from "contracts/api/crawl/run-read.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { healthState, SIGNAL_THRESHOLDS } from "contracts/report/health";
import { For, Show } from "solid-js";
import { pluralize } from "../../ui/format";
import { healthLabel, healthVar } from "../../ui/health";
import { Metric } from "../../ui/Metric";
import { Sparkline } from "../../ui/Sparkline";
import { isOpen } from "../website/issueModel";
import { createWebsiteStore } from "../website/store";
import { ContentFitnessProvenance } from "./ContentFitnessProvenance";
import { buildExecutiveSummary, formatScoreDelta } from "./executiveSummary";
import { ModuleShell } from "./ModuleShell";
import { ModuleCard } from "./overviewCards";
import { overviewScanAvailability } from "./scanAvailability";
import {
  createCompletedRunForReport,
  type ScanCoverage,
  scanCoverage,
} from "./scanCoverage";
import { contentFitnessHeadline, estateStats } from "./scanModel";

export function SignalOverview(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  const data = () => context.data();
  const completedRun = createCompletedRunForReport(props.websiteId, data);

  return (
    <ModuleShell
      context={context}
      activeModule="overview"
      workspaceClass="signal-overview-workspace"
    >
      <Show
        when={data()}
        keyed
        fallback={<div class="loading signal-loading">Loading overview...</div>}
      >
        {(value) => (
          <SignalOverviewContent
            data={value}
            websiteId={props.websiteId}
            coverage={scanCoverage(value, completedRun())}
            completedRun={completedRun()}
          />
        )}
      </Show>
    </ModuleShell>
  );
}

function SignalOverviewContent(props: {
  data: OverviewResponse;
  websiteId: string;
  coverage: ScanCoverage;
  completedRun: RunSummary | null;
}) {
  const summary = () => buildExecutiveSummary(props.data, props.websiteId);
  const headline = () => contentFitnessHeadline(props.data);
  const scoreState = () =>
    healthState(
      headline().score,
      SIGNAL_THRESHOLDS.good,
      SIGNAL_THRESHOLDS.watch,
    );
  const stats = () => estateStats(props.data);
  const scanState = () => overviewScanAvailability(props.data);

  return (
    <div class="signal-overview-page">
      <header class="signal-overview-head">
        <nav class="signal-crumbs" aria-label="Faro Signal hierarchy">
          <span>Faro Signal</span>
          <span>/</span>
          <span>Overview</span>
        </nav>
        <div>
          <h1>Overview</h1>
          <p>
            Cross-module executive synthesis across Faro Signal. Content Fitness
            uses the latest completed scored run when one is available.
          </p>
        </div>
      </header>

      <section class="signal-overview-grid">
        <article class="signal-executive-card">
          <span class="signal-card-label">Executive summary</span>
          <h2>{summary().headline}</h2>
          <For each={summary().summary}>{(line) => <p>{line}</p>}</For>
          <Show when={summary().priorities.length > 0}>
            <div class="signal-overview-priorities">
              <span class="signal-card-label">Highest-priority actions</span>
              <For each={summary().priorities}>
                {(priority) => (
                  <a class="signal-priority-row" href={priority.href}>
                    <span>
                      <strong>{priority.title}</strong>
                      <small>{priority.detail}</small>
                    </span>
                    <b>{priority.action}</b>
                  </a>
                )}
              </For>
            </div>
          </Show>
        </article>

        <aside class={`signal-overview-score is-${scoreState()}`}>
          <div>
            <span class="signal-card-label">Content Fitness score</span>
            <div class="signal-overview-score-number">
              <strong>{headline().score ?? "--"}</strong>
              <span>/100</span>
            </div>
            <span class={`signal-score-status is-${scoreState()}`}>
              {healthLabel(scoreState())}
            </span>
            <Show when={headline().scoreDelta !== null}>
              <small>
                {formatScoreDelta(headline().scoreDelta ?? 0)} since previous
                scored run
              </small>
            </Show>
            <ContentFitnessProvenance
              data={props.data}
              completedRun={props.completedRun}
            />
          </div>
          <div class="signal-overview-trend">
            <div
              class="signal-overview-trend-head"
              title="Recent scored runs can use different scopes or frameworks, which can affect comparability."
            >
              <span>Trend across recent scans</span>
              <Show when={headline().trend.length > 0}>
                <b>
                  {headline().trend.length}{" "}
                  {headline().trend.length === 1 ? "point" : "points"}
                </b>
              </Show>
            </div>
            <Sparkline
              data={headline().trend}
              width={420}
              height={120}
              color={healthVar(scoreState())}
              target={props.data.benchmark?.target ?? null}
              strokeWidth={2.4}
              id={`signal-overview-${props.websiteId}`}
            />
          </div>
        </aside>
      </section>

      <Show when={summary().changes.length > 0}>
        <section class="signal-overview-changes">
          <div class="signal-panel-head">
            <div>
              <h2>What changed</h2>
              <p>Movement recorded between completed scored runs.</p>
            </div>
          </div>
          <div class="signal-change-list">
            <For each={summary().changes}>
              {(change) => <span>{change}</span>}
            </For>
          </div>
        </section>
      </Show>

      <section class="signal-module-grid-overview">
        <ModuleCard
          title="Scan"
          state={scanState().label}
          detail={scanCoverageDetail(props.coverage, props.data)}
          meta={scanState().detail}
          href={`/w/${props.websiteId}/scan`}
          cta="Open Scan"
        >
          <div class="signal-module-metrics">
            <Metric
              label="Content Fitness score"
              value={`${headline().score ?? "--"}/100`}
            />
            <Metric
              label="Needs attention"
              value={`${stats().weakPageCount}`}
            />
            <Metric
              label="Priority links"
              value={`${stats().importantPageCount}`}
            />
          </div>
        </ModuleCard>
        <ModuleCard
          title="Map"
          state="Architecture Preview available"
          detail="Turn the current Scan inventory into editable draft groups, decisions and migration direction."
          meta="Preview changes reset on reload"
          href={`/w/${props.websiteId}/map`}
          cta="Open Map Preview"
        />
        <ModuleCard
          title="Brief"
          state="Delivery Preview available"
          detail="Turn a supported Scan issue or accepted Map change into a focused delivery specification."
          meta="Preview changes are not saved"
          href={`/w/${props.websiteId}/brief`}
          cta="Open Brief Preview"
        />
        <ModuleCard
          title="Govern"
          state="Govern Preview available"
          detail="Review issue readiness and verification state in a workflow-focused preview."
          meta="Preview reflects current Scan evidence"
          href={`/w/${props.websiteId}/govern`}
          cta="Open Govern Preview"
        />
      </section>
    </div>
  );
}

function scanCoverageDetail(
  coverage: ScanCoverage,
  data: OverviewResponse,
): string {
  const issueCount = data.issues.filter(isOpen).length;
  const failed =
    (coverage.failedPages ?? 0) > 0
      ? ` / ${coverage.failedPages} not completed`
      : "";
  return `${coverage.scoredPages} of ${coverage.inventoryPages} inventory ${pluralize(coverage.inventoryPages, "page")} scored${failed} / ${issueCount} open ${pluralize(issueCount, "issue")}`;
}
